/**
 * @file MfaForm.js
 * @description Unified MFA form for sending and verifying codes
 */

import React, { useState, useEffect } from 'react';
import { sendMfaCode, requestToken } from '../../services/authService';
import Toast from '../ui/toasts/Toast';
import { useTheme } from '../../store/ThemeContext';
import PlaceholderLogo from '../ui/PlaceholderLogo';
import style from './AuthPage.module.scss';
import { getText } from '../../i18n';

function MfaForm({
    onSuccess,
    handleAuthError,
    authData,
    onBeforeSubmit = null,
}) {
    const themeStyles = useTheme();
    const [mfaCode, setMfaCode] = useState('');
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [isVerifyingCode, setIsVerifyingCode] = useState(false);
    const [mfaCodeClassName, setMfaCodeClassName] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [selectedDestination, setSelectedDestination] = useState('');
    const RESEND_CODE_COUNTDOWN = 60; // time in seconds

    useEffect(() => {
        let timer = null;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleSendCodeClick = async destination => {
        if (!destination) {
            Toast.error(getText('msg_84f7e7d1e93d'));
            return;
        }
        if (countdown > 0) return;

        handleAuthError('', false);
        setIsSendingCode(true);
        try {
            const method = destination.includes('phone') ? 'sms' : 'email';
            await sendMfaCode(
                authData.username,
                authData.password,
                method,
                destination,
                authData.companyCode,
            );
            setCountdown(RESEND_CODE_COUNTDOWN);
            setVerifyingCode(true);
            authData.selectedMethod = method;
        } catch (error) {
            console.error('Error sending MFA code:', error);
            Toast.error(
                getText('msg_4be3ad9cc67b'),
            );
        }
        setIsSendingCode(false);
    };

    const handleSubmit = async event => {
        event.preventDefault();
        if (onBeforeSubmit) onBeforeSubmit();
        if (isVerifyingCode) return;
        if (validateForm()) {
            try {
                setIsVerifyingCode(true);
                const response = await requestToken(
                    authData.username,
                    authData.password,
                    authData.companyCode,
                    authData.rememberMe,
                    authData.selectedMethod,
                    mfaCode,
                );
                onSuccess(response);
            } catch (error) {
                console.error('Error verifying MFA code:', error.code);
                if (error.code === 'ERR_BAD_REQUEST') {
                    Toast.error(
                        getText('msg_53de8ad77d01'),
                    );
                }
            } finally {
                setIsVerifyingCode(false);
            }
        }
    };

    const validateForm = () => {
        let isFormValid = true;
        if (!mfaCode.trim()) {
            setMfaCodeClassName('is-invalid');
            isFormValid = false;
        } else {
            setMfaCodeClassName('');
        }
        return isFormValid;
    };

    const handleChange = event => {
        setMfaCode(event.target.value);
        setMfaCodeClassName('');
    };

    const getMethodLabel = (method, mfaMethods) => {
        switch (method) {
            case 'primary_email':
                return mfaMethods.primary_email || 'Not set';
            case 'primary_phone':
                return mfaMethods.primary_phone || 'Not set';
            case 'optional_email':
                return mfaMethods.optional_email || 'Not set';
            case 'optional_phone':
                return mfaMethods.optional_phone || 'Not set';
            default:
                return 'Unknown';
        }
    };

    const renderMfaMethodsSelection = () => {
        const availableMethods = [];

        if (authData.mfaMethods.methods.includes('email')) {
            if (authData.mfaMethods.primary_email) {
                availableMethods.push('primary_email');
            }

            if (
                authData.mfaMethods.optional_email &&
                `${authData.mfaMethods.primary_email}`.toLowerCase() !==
                    `${authData.mfaMethods.optional_email}`.toLowerCase()
            ) {
                availableMethods.push('optional_email');
            }
        }

        if (authData.mfaMethods.methods.includes('sms')) {
            if (authData.mfaMethods.primary_phone) {
                availableMethods.push('primary_phone');
            }
            if (
                authData.mfaMethods.optional_phone &&
                `${authData.mfaMethods.primary_phone}` !==
                    `${authData.mfaMethods.optional_phone}`
            ) {
                availableMethods.push('optional_phone');
            }
        }

        return (
            <div className="mfa-methods-container text-left py-3 px-3">
                <div className="form-group mb-4 d-table mx-auto mt-3">
                    {availableMethods.map(method => (
                        <div className="form-check mb-3" key={method}>
                            <input
                                className="form-check-input"
                                type="radio"
                                name="mfaMethod"
                                id={method}
                                value={method}
                                checked={selectedDestination === method}
                                onChange={e =>
                                    setSelectedDestination(e.target.value)
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor={method}
                            >
                                {getMethodLabel(method, authData.mfaMethods)}
                            </label>
                        </div>
                    ))}
                </div>
                {selectedDestination.includes('phone') && (
                    <p className="text-muted small mb-3">
                        {getText('msg_f69451d64e8f')}
                    </p>
                )}
                <button
                    className="ing-button-primary-left mx-auto"
                    onClick={() => handleSendCodeClick(selectedDestination)}
                    disabled={isSendingCode || countdown > 0}
                >
                    {isSendingCode
                        ? 'Sending...'
                        : `Send${countdown > 0 ? ` (${countdown}s)` : ''}`}
                </button>
                <p className="text-center mt-3">
                    <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                </p>
            </div>
        );
    };

    if (!verifyingCode) {
        // ========= REQUEST MFA CODE FORM STARTS =========
        return (
            <>
                <small
                    className={`${style.returnToLoginButton} position-absolute`}
                    onClick={() => handleAuthError('', true)}
                >
                    <i className="bi bi-chevron-left ms-3" /> {getText('msg_b5cd32158b70')}
                </small>
                <div
                    className={`form-group text-center mb-3 ${style.logoContainer}`}
                >
                    {themeStyles?.primaryLogo ? (
                        <img
                            src={themeStyles.primaryLogo}
                            alt={getText('msg_101d2406160e')}
                            className="img-fluid"
                        />
                    ) : (
                        <PlaceholderLogo />
                    )}
                </div>
                <div className="form-group text-center">
                    <h4 className="mt-3 mb-3">
                        {getText('msg_46baaa872952')}
                    </h4>
                    <p>
                        {getText('msg_4d56c1f82dca')}
                    </p>
                </div>

                {renderMfaMethodsSelection()}
            </>
        );
        // ========= REQUEST MFA CODE FORM ENDS =========
    } else {
        // ========= CODE CONFIRMATION FORM STARTS =========
        return (
            <form
                className="needs-validation p-3"
                onSubmit={handleSubmit}
                noValidate
            >
                <div
                    className={`form-group text-center mb-3 ${style.logoContainer}`}
                >
                    {themeStyles?.primaryLogo ? (
                        <img
                            src={themeStyles.primaryLogo}
                            alt={getText('msg_101d2406160e')}
                            className="img-fluid"
                        />
                    ) : (
                        <PlaceholderLogo />
                    )}
                </div>
                <div className="form-group text-center mb-4">
                    <i
                        className="bi bi-check-circle"
                        style={{ fontSize: '3rem', color: '#4CAF50' }}
                    />
                    <h4 className="mt-3 mb-2 fw-bold">{getText('msg_29f802af62f3')}</h4>
                    <p className="text-muted">
                        {getText('msg_689b40397cc4')}
                    </p>
                </div>
                <div className="form-group mb-4">
                    <label htmlFor="mfaCode" className="form-label fw-semibold">
                        {getText('msg_50cdc18b7b3e')} <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${mfaCodeClassName}`}
                        placeholder={getText('msg_601202604470')}
                        name="mfaCode"
                        id="mfaCode"
                        autoComplete="off"
                        value={mfaCode}
                        onChange={handleChange}
                        aria-describedby="code-icon"
                        ref={input => input && input.focus()}
                    />
                    <div className="invalid-feedback">{getText('msg_d1f6cb46b21d')}</div>
                </div>
                <div className="form-group mb-3">
                    <button
                        type="submit"
                        className={`ing-button-primary-left mx-auto btn-${
                            isVerifyingCode
                                ? 'secondary cursor-not-allowed'
                                : 'primary'
                        }`}
                    >
                        {isVerifyingCode ? (
                            'Validating...'
                        ) : (
                            <>
                                {getText('msg_bc981983e7f5')} <i className="bi bi-chevron-right" />
                            </>
                        )}
                    </button>
                </div>
                <div className="text-center pt-3">
                    <p className="fw-bold mb-1">
                        {getText('msg_f36c28b77cd9')}
                    </p>
                    <div className="d-flex justify-content-center align-items-center gap-2">
                        <button
                            type="button"
                            className={`btn btn-link p-0${countdown > 0 ? ' text-muted' : ''}`}
                            onClick={() =>
                                handleSendCodeClick(selectedDestination)
                            }
                            disabled={isSendingCode || countdown > 0}
                        >
                            {isSendingCode
                                ? 'Sending...'
                                : `Resend Code${countdown > 0 ? ` (${countdown}s)` : ''}`}
                        </button>
                        <span>|</span>
                        <a
                            href={`/get-support${authData.companyCode ? `?companyCode=${authData.companyCode}` : ''}`}
                        >
                            {getText('msg_8a38384386fa')}
                        </a>
                    </div>
                </div>
                <p className="text-center mt-3">
                    <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                </p>
            </form>
        );
        // ========= CODE CONFIRMATION FORM ENDS =========
    }
}

export default MfaForm;
